/**
 * Dev panel — minimal read-only diagnostics page.
 *
 * Gated by `env.devPanelEnabled` (VITE_REEFAPP_DEV=1). Polls the agent's
 * `/healthz` and `/state` endpoints once a second and shows connectivity
 * plus the raw snapshot JSON for quick eyeball debugging.
 *
 * Monitor-only build: all scenario/override/control surfaces removed.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { devApi } from '../lib/dev-api';
import { loadPublicEnv } from '../lib/env';
import type { AgentSnapshot } from '../lib/types';

export function DevPage() {
  const env = loadPublicEnv();
  const [healthy, setHealthy] = useState<boolean | null>(null);
  const [snapshot, setSnapshot] = useState<AgentSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [hz, st] = await Promise.all([devApi.healthz(), devApi.state()]);
    setHealthy(hz.ok ? hz.value.ok : false);
    if (st.ok) setSnapshot(st.value);
    if (!hz.ok) setError(hz.error);
    else if (!st.ok) setError(st.error);
    else setError(null);
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), 1000);
    return () => window.clearInterval(id);
  }, [refresh]);

  if (!env.devPanelEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Dev panel disabled</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-reef-300">
              Set <code className="text-reef-100">VITE_REEFAPP_DEV=1</code> in your <code>.env</code> to enable.
            </p>
            <div className="mt-3">
              <Link to="/">
                <Button>Back to dashboard</Button>
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  const apex = snapshot?.apex ?? null;

  return (
    <div className="min-h-screen px-6 py-8 max-w-5xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold tracking-tight">Dev panel</h1>
          <Badge variant="warn">{env.agentDevUrl}</Badge>
        </div>
        <Link to="/">
          <Button variant="ghost">Back</Button>
        </Link>
      </header>

      {error ? (
        <div className="mb-4 rounded-md border border-coral-500/40 bg-coral-500/10 px-3 py-2 text-xs text-coral-200">
          {error}
        </div>
      ) : null}

      <section className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Agent status</CardTitle>
          </CardHeader>
          <CardBody>
            <dl
              className="text-sm text-reef-200 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1"
              data-testid="dev-agent-status"
            >
              <dt className="text-reef-400">Health</dt>
              <dd data-testid="dev-healthz">
                {healthy === null ? (
                  'checking…'
                ) : healthy ? (
                  <Badge variant="ok" live>healthy</Badge>
                ) : (
                  <Badge variant="danger">unreachable</Badge>
                )}
              </dd>
              <dt className="text-reef-400">Tank</dt>
              <dd data-testid="dev-tank-id">{snapshot?.tankId ?? '—'}</dd>
              <dt className="text-reef-400">Snapshot at</dt>
              <dd data-testid="dev-snapshot-ts">{snapshot?.ts ?? '—'}</dd>
              <dt className="text-reef-400">Apex</dt>
              <dd data-testid="dev-apex-summary">
                {apex
                  ? `fetched ${new Date(apex.fetchedAtMs).toLocaleTimeString()} · ${String(apex.probes.length)} probes · ${String(apex.outlets.length)} outlets`
                  : 'not configured'}
              </dd>
            </dl>
          </CardBody>
        </Card>
      </section>

      <section>
        <Card>
          <CardHeader>
            <CardTitle>Snapshot</CardTitle>
          </CardHeader>
          <CardBody>
            <pre className="overflow-x-auto rounded-md bg-reef-950/80 p-3 text-xs text-reef-200">
              {snapshot ? JSON.stringify(snapshot, null, 2) : '// no snapshot yet'}
            </pre>
          </CardBody>
        </Card>
      </section>
    </div>
  );
}
